/**
 * IframePreview Component
 *
 * Renders the user's website in an iframe with the editor script injected.
 * Handles device-specific sizing and zoom levels.
 *
 * Before mounting the iframe, asks the backend to probe the target URL's
 * security headers (X-Frame-Options, CSP frame-ancestors). If blocking
 * headers are detected the blocked overlay is shown immediately — no
 * countdown needed.  If the check times out or errors we fall back to
 * mounting the iframe with a 5-second load timeout.
 */

import { forwardRef, useCallback, useState, useEffect, useRef as useReactRef } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { HugeiconsIcon } from "@hugeicons/react"
import { Link01Icon } from "@hugeicons/core-free-icons"
import { checkEmbedUrl } from '@/lib/api/api'
import { selectIframeBaseUrl, isTrustedResolvedUrl } from './iframeUrl'

type DeviceType = 'desktop' | 'tablet' | 'mobile'

interface IframePreviewProps {
  pageUrl: string
  device: DeviceType
  zoom: number
  onLoad?: () => void
  onBlocked?: (reason: 'csp' | 'timeout' | 'error') => void
  /** Fired when the backend resolves a canonical (post-redirect) URL. */
  onResolvedUrl?: (finalUrl: string) => void
  suppressBlockedOverlay?: boolean
  className?: string
  draftId?: string
  authToken?: string
  // Optional override for dynamic viewport sizing (takes precedence over device)
  viewportWidth?: number
  viewportHeight?: number
}

const DEVICE_SIZES: Record<DeviceType, { width: number; height: number }> = {
  desktop: { width: 1440, height: 900 },
  tablet:  { width: 768,  height: 1024 },
  mobile:  { width: 375,  height: 812 },
}

const EDITOR_SCRIPT_URL = `${window.location.origin}/hmblEditorReact.js`
const IFRAME_LOAD_TIMEOUT = 5000   // fallback if check was inconclusive

export const IframePreview = forwardRef<HTMLIFrameElement, IframePreviewProps>(
  ({ pageUrl, device, zoom, onLoad, onBlocked, onResolvedUrl, suppressBlockedOverlay, className, draftId, authToken, viewportWidth, viewportHeight }, ref) => {
    const [isLoading,         setIsLoading]         = useState(true)
    // 'checking' while the backend probe is in-flight; iframe not mounted yet
    const [isChecking,        setIsChecking]         = useState(true)
    const [error,             setError]              = useState<string | null>(null)
    const [iframeBlocked,     setIframeBlocked]      = useState(false)
    const [hasOpenedInNewWindow, setHasOpenedInNewWindow] = useState(false)
    // Canonical post-redirect URL from the backend probe (null = use pageUrl).
    const [resolvedUrl,       setResolvedUrl]        = useState<string | null>(null)

    const loadTimeoutRef  = useReactRef<ReturnType<typeof setTimeout> | null>(null)
    const hasLoadedRef    = useReactRef(false)
    // Prevent stale async results from a previous pageUrl from applying
    const checkAbortRef   = useReactRef(false)

    const deviceSize = viewportWidth && viewportHeight
      ? { width: viewportWidth, height: viewportHeight }
      : DEVICE_SIZES[device]
    const scale = zoom / 100

    // Build the iframe URL with editor params. Prefer the canonical
    // (post-redirect) URL from the backend probe when available so the page
    // loads at its final origin and never redirects inside the iframe — which
    // is what would otherwise break the postMessage handshake and/or strip the
    // editor query params.
    const buildIframeUrl = useCallback(() => {
      const base = selectIframeBaseUrl(resolvedUrl, pageUrl)
      const addParams = (url: URL) => {
        url.searchParams.set('hmblEditor', '1')
        url.searchParams.set('experimental', 'true')
        if (draftId)    url.searchParams.set('testId',     draftId)
        if (authToken)  url.searchParams.set('authToken',  authToken)
        return url.toString()
      }
      try {
        return addParams(new URL(base))
      } catch {
        if (base.startsWith('/')) return addParams(new URL(`${window.location.origin}${base}`))
        return addParams(new URL(`https://${base}`))
      }
    }, [resolvedUrl, pageUrl, draftId, authToken])

    // Open page in new window as fallback
    const openInNewWindow = useCallback(() => {
      const editorUrl = buildIframeUrl()
      const newWindow = window.open(editorUrl, '_blank', 'noopener,noreferrer')
      if (newWindow) {
        setHasOpenedInNewWindow(true)
        toast.success('Editor opened in new window', {
          description: 'Continue editing in the new window. Changes will be saved automatically.',
        })
      } else {
        toast.error('Failed to open new window', {
          description: 'Please check your browser popup blocker settings.',
        })
      }
    }, [buildIframeUrl])

    // Immediately block + surface the error message
    const triggerBlocked = useCallback((message: string, reason: 'csp' | 'timeout' | 'error') => {
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
      setIsLoading(false)
      setIsChecking(false)
      setIframeBlocked(true)
      setError(message)
      onBlocked?.(reason)
    }, [onBlocked])

    // Main effect: probe headers first, then (if OK) mount iframe with fallback timeout
    useEffect(() => {
      // Reset state for new URL
      hasLoadedRef.current  = false
      checkAbortRef.current = false
      setIsLoading(true)
      setIsChecking(true)
      setError(null)
      setIframeBlocked(false)
      setHasOpenedInNewWindow(false)
      setResolvedUrl(null)
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)

      // Resolve the full URL to probe (relative paths → absolute)
      let probeUrl: string
      try {
        probeUrl = pageUrl.startsWith('/')
          ? `${window.location.origin}${pageUrl}`
          : new URL(pageUrl).toString()
      } catch {
        probeUrl = pageUrl
      }

      const runCheck = async () => {
        // Only run if we have a user token to authenticate the request
        if (authToken) {
          try {
            const result = await checkEmbedUrl(probeUrl, authToken)
            if (checkAbortRef.current) return   // URL changed while we were waiting

            // Adopt the canonical post-redirect URL when the backend provides
            // one (feature-detected — absent on older backends). Loading the
            // iframe at its final origin avoids the in-iframe redirect that
            // breaks the handshake. Gate it: only adopt a same-site http(s)
            // URL — the iframe URL carries the authToken and seeds the bridge's
            // trusted postMessage origin, so a cross-origin/open-redirect
            // finalUrl must never be used. (Probe-side SSRF safety is the
            // backend's job; this is the client backstop.)
            if (result.finalUrl && isTrustedResolvedUrl(result.finalUrl, pageUrl)) {
              setResolvedUrl(result.finalUrl)
              onResolvedUrl?.(result.finalUrl)
            }

            if (result.canEmbed === false) {
              triggerBlocked(
                `Your website has security headers (${result.reason}) that prevent iframe embedding. Opening in a new window instead.`,
                'csp'
              )
              return
            }
            // canEmbed === true → we're confident; skip the long fallback timeout
            // canEmbed === null → inconclusive; proceed with iframe + fallback timeout
          } catch {
            if (checkAbortRef.current) return
            // Backend check failed — fall through to iframe + timeout
          }
        }

        if (checkAbortRef.current) return

        // Probe done (or skipped) — mount the iframe and start the fallback timer
        setIsChecking(false)

        loadTimeoutRef.current = setTimeout(() => {
          if (!hasLoadedRef.current) {
            triggerBlocked(
              'The page did not load in the expected time. This usually means the site has security settings that prevent embedding.',
              'timeout'
            )
          }
        }, IFRAME_LOAD_TIMEOUT)
      }

      runCheck()

      return () => {
        checkAbortRef.current = true
        if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
      }
    }, [pageUrl, authToken])

    // Handle iframe load success
    const handleLoad = useCallback(() => {
      if (loadTimeoutRef.current) clearTimeout(loadTimeoutRef.current)
      hasLoadedRef.current = true
      setIsLoading(false)
      setError(null)
      setIframeBlocked(false)

      // Inject editor script
      const iframe = (ref as React.RefObject<HTMLIFrameElement>)?.current
      if (iframe?.contentWindow) {
        try {
          if (!iframe.contentDocument?.getElementById('hmbl-editor-script')) {
            const script = iframe.contentDocument?.createElement('script')
            if (script) {
              script.id  = 'hmbl-editor-script'
              script.src = EDITOR_SCRIPT_URL
              script.async = true
              iframe.contentDocument?.head.appendChild(script)
            }
          }
        } catch {
          // Cross-origin — script injected via postMessage by the bridge
        }
      }

      onLoad?.()
    }, [ref, onLoad])

    // Handle iframe hard error
    const handleError = useCallback(() => {
      triggerBlocked('Failed to load page. The page may not allow embedding.', 'error')
    }, [triggerBlocked])

    const iframeStyles = {
      width:  viewportWidth  ? deviceSize.width  : '100%',
      height: viewportHeight ? deviceSize.height : '100%',
    }

    return (
      <div className={cn('flex items-start justify-center', !viewportWidth && 'h-full', viewportWidth && 'overflow-auto p-8', className)}>
        <div
          className={cn(
            "relative bg-white shadow-2xl",
            viewportWidth ? "rounded-lg overflow-hidden" : "w-full h-full"
          )}
          style={{
            width:  viewportWidth  ? deviceSize.width  : '100%',
            height: viewportHeight ? deviceSize.height : '100%',
            ...(scale !== 1 ? { transform: `scale(${scale})`, transformOrigin: 'top center' } : {}),
          }}
        >
          {/* Device frame */}
          {device !== 'desktop' && !iframeBlocked && (
            <div className="absolute inset-0 pointer-events-none z-10">
              <div className={cn(
                'absolute inset-0 border-neutral-800 rounded-3xl pointer-events-none',
                device === 'mobile' ? 'border-[12px]' : 'border-[16px]'
              )} />
              {device === 'mobile' && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-neutral-800 rounded-b-2xl" />
              )}
              {device === 'mobile' && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-neutral-600 rounded-full" />
              )}
            </div>
          )}

          {/* Loading / checking overlay */}
          {(isLoading || isChecking) && !iframeBlocked && (
            <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 z-20">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-neutral-600 text-sm">
                  {isChecking ? 'Checking iframe compatibility…' : 'Loading page in iframe…'}
                </p>
              </div>
            </div>
          )}

          {/* Blocked overlay */}
          {iframeBlocked && error && !hasOpenedInNewWindow && !suppressBlockedOverlay && (
            <div className="absolute inset-0 flex items-center justify-center bg-neutral-50 z-20">
              <div className="text-center p-8 max-w-md">
                <div className="w-16 h-16 mx-auto mb-4 text-orange-500">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4M12 16h.01" />
                  </svg>
                </div>
                <p className="text-neutral-800 font-semibold mb-2 text-lg">Cannot Load in Iframe</p>
                <p className="text-neutral-600 text-sm mb-6">{error}</p>

                <Button onClick={openInNewWindow} className="gap-2" size="lg">
                  <HugeiconsIcon icon={Link01Icon} size={16} />
                  Open Editor in New Window
                </Button>

                <div className="mt-6 pt-6 border-t border-neutral-200">
                  <p className="text-xs text-neutral-500 mb-3">
                    <strong>Why is this happening?</strong>
                  </p>
                  <p className="text-xs text-neutral-500 text-left">
                    Your website has security headers (X-Frame-Options or Content-Security-Policy)
                    that prevent it from being embedded in an iframe. Opening in a new window allows
                    the editor to work with your page directly.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Editing in new window state */}
          {hasOpenedInNewWindow && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-orange-50 to-purple-50 z-20">
              <div className="text-center p-8 max-w-lg">
                <div className="w-20 h-20 mx-auto mb-6 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-purple-500 rounded-2xl opacity-20 animate-pulse" />
                  <div className="relative flex items-center justify-center h-full">
                    <HugeiconsIcon icon={Link01Icon} size={40} className="text-orange-600" />
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-neutral-800 mb-3">Editing in New Window</h3>

                <p className="text-neutral-600 text-sm mb-6">
                  The editor is now open in a separate window. Make your changes there,
                  and they'll be automatically saved to this draft.
                </p>

                <div className="bg-white/70 backdrop-blur-sm border border-neutral-200 rounded-lg p-4 mb-6">
                  {[
                    { color: 'orange', text: 'Edit elements, text, styles, and layout in the new window' },
                    { color: 'purple', text: `Changes are saved automatically to draft #${draftId?.substring(0, 8)}` },
                    { color: 'green',  text: 'Return here and click "Back" to continue creating your experiment' },
                  ].map(({ color, text }, i) => (
                    <div key={i} className={cn('flex items-start gap-3 text-left', i > 0 && 'mt-3')}>
                      <div className={`w-6 h-6 rounded-full bg-${color}-100 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <span className={`text-${color}-600 text-xs font-semibold`}>{i + 1}</span>
                      </div>
                      <p className="text-xs text-neutral-700">{text}</p>
                    </div>
                  ))}
                </div>

                <Button onClick={openInNewWindow} variant="outline" className="gap-2">
                  <HugeiconsIcon icon={Link01Icon} size={16} />
                  Reopen editor window
                </Button>
              </div>
            </div>
          )}

          {/* Iframe — only mounted after the embed check completes */}
          {!iframeBlocked && !isChecking && (
            <iframe
              ref={ref}
              src={buildIframeUrl()}
              style={iframeStyles}
              className="w-full h-full border-0 block"
              onLoad={handleLoad}
              onError={handleError}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              title="Page Preview"
            />
          )}
        </div>
      </div>
    )
  }
)

IframePreview.displayName = 'IframePreview'

export default IframePreview
